'use client';

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

import Link from "next/link";
import { useContext, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";

import { DailyDealBadge } from "@/components/Inventory/Badges/DailyDealBadge";
import { FlashSaleBadge } from "@/components/Inventory/Badges/FlashSaleBadge";
import { InventoryTypeBadge } from "@/components/Inventory/Badges/InventoryTypeBadge";
import { InventoryImageSlider } from "@/components/Inventory/InventoryImageSlider";
import { InventoryPricing } from "@/components/Inventory/InventoryPricing";
import {
  INVENTORY_BIG_IMAGE_HEIGHT,
  INVENTORY_BIG_IMAGE_WIDTH,
  PDP_MINIATURE_WIDTH_REM
} from "@/constants/inventory";
import { LayoutContext } from "@/contexts/LayoutContext";
import { useIsFavorite } from "@/hooks/useIsFavorite";
import { useShareInventory } from "@/hooks/useShareInventory";
import { getImageUrl, getUnlockPriceLabel } from "@/tools/inventory";
import { faHeart as faHeartOutline } from "@fortawesome/free-regular-svg-icons";
import { faHeart as faHeartSolid } from "@fortawesome/free-solid-svg-icons";
import {
  faArrowLeft,
  faArrowRight,
  faLocationDot,
  faPhone,
  faShareFromSquare
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IInventory, InventoryField } from "@lamesarv-sdk/types";

import { ImageActions } from "./ImageActions";

interface DesktopOverlayProps {
  item: IInventory;
  closeOverlay: () => void;
}

const DesktopOverlay = ({ item, closeOverlay }: DesktopOverlayProps) => {
  const {
    context: { inventoryLocations: locations, detailsSettings: settings },
  } = useContext(LayoutContext);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { isFavorite, toggleFavorite } = useIsFavorite(item);
  const { canShare, handleShare } = useShareInventory(item);

  const location = item[InventoryField.location]
    ? locations?.find((location) => location.aliases.includes(item[InventoryField.location].toLowerCase().trim()))
    : undefined;

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
  };

  return (
    <div className="max-lg:hidden fixed w-screen h-screen top-0 left-0 z-50">
      <div
        className="bg-black-500 bg-opacity-60 fixed w-screen h-screen top-0 left-0"
        onClick={closeOverlay}
        role="none"
      />

      <Lightbox
        open={isLightboxOpen}
        close={() => setIsLightboxOpen(false)}
        slides={item[InventoryField.imageCloudinaryIds].map((publicId) => ({
          src: getImageUrl(publicId, 900, 750, {
            width: 900,
            height: 750,
          }),
          width: 900,
          height: 750,
        }))}
        index={selectedImageIndex}
        animation={{
          swipe: 0,
        }}
        styles={{
          container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
        }}
        className="[&_img]:w-[900px] [&_img]:h-[750px]"
        plugins={[Thumbnails]}
        carousel={{
          preload: 10,
        }}
        thumbnails={{
          border: 0,
          gap: 20,
          width: 100,
          height: 100,
          padding: 0,
        }}
      />

      <div className="container mx-auto xl:max-w-6xl h-screen bg-white flex flex-col relative">
        <div className="p-8 flex justify-between items-center border-b border-black-300">
          <button type="button" onClick={closeOverlay}>
            <FontAwesomeIcon icon={faArrowLeft} className="size-5" />
          </button>
          <span className="text-2xl font-bold">{item[InventoryField.title]}</span>
          <div className="flex items-center gap-4">
            {canShare && (
              <button type="button" onClick={handleShare} className="flex items-center gap-1">
                <FontAwesomeIcon icon={faShareFromSquare} className="size-5" />
                <span>Share</span>
              </button>
            )}
            <button type="button" onClick={toggleFavorite} className="flex items-center gap-1 group">
              <FontAwesomeIcon
                icon={isFavorite ? faHeartSolid : faHeartOutline}
                className={twMerge('size-5', isFavorite ? 'text-red-500' : 'group-hover:text-red-500')}
              />
              <span>Favorite</span>
            </button>
          </div>
        </div>
        <div className="flex-1 flex">
          <div className="flex-1 relative">
            <div className="absolute top-0 left-0 size-full overflow-y-auto">
              <div className="grid grid-cols-2 gap-1.5">
                <img
                  src={getImageUrl(
                    item[InventoryField.imageCloudinaryIds][0],
                    INVENTORY_BIG_IMAGE_WIDTH,
                    INVENTORY_BIG_IMAGE_HEIGHT,
                  )}
                  alt=""
                  className="col-span-2 object-cover w-full hover:brightness-[.8] cursor-pointer"
                  onClick={() => openLightbox(0)}
                  role="presentation"
                />
                {item[InventoryField.imageCloudinaryIds].slice(1).map((publicId, index) => (
                  <img
                    key={publicId}
                    src={getImageUrl(publicId, INVENTORY_BIG_IMAGE_WIDTH, INVENTORY_BIG_IMAGE_HEIGHT)}
                    alt=""
                    className="object-cover w-full hover:brightness-[.8] cursor-pointer"
                    onClick={() => openLightbox(index + 1)}
                    role="presentation"
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="mt-8 mx-6 flex flex-col min-w-[250px]">
            <div className="flex gap-3">
              <InventoryTypeBadge inventoryType={item[InventoryField.inventoryType]} />
              {item[InventoryField.isFlashSale] && <FlashSaleBadge />}
              {item[InventoryField.isDailyDeal] && <DailyDealBadge />}
            </div>
            <div className="flex items-center gap-4 text-black-350 text-xs sm:text-sm font-medium mt-3">
              <span>Stock# {item[InventoryField.stockNumber]}</span>
              <div className="flex gap-0.5 items-center">
                <FontAwesomeIcon icon={faLocationDot} className="size-4" />
                <span>{item[InventoryField.location]}</span>
              </div>
            </div>
            <InventoryPricing item={item} className="mt-5" />
            <a
              href="#pdpform"
              onClick={closeOverlay}
              className="rounded bg-blue-500 text-white font-bold py-3 flex items-center justify-center max-lg:hidden mt-5 hover:bg-blue-600"
            >
              {getUnlockPriceLabel(item).buttonLabel}
            </a>
            <div className="hidden lg:flex gap-2.5 mt-5">
              <a
                href="tel:877-641-8052"
                className="py-2 px-6 flex items-center justify-center gap-1.5 border border-black-200 rounded-sm hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faPhone} className="size-4" />
                <span>Let's talk</span>
              </a>
              <Link
                prefetch
                href={location ? `/${location.route}` : '#'}
                className="py-2 px-6 flex items-center justify-center gap-1.5 border border-black-200 rounded-sm hover:bg-gray-50"
              >
                <span className="text-sm text-black-500">Get Directions</span>
                <FontAwesomeIcon icon={faArrowRight} className="size-4 text-black-500" />
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-2.5">
              <div className="bg-blue-100 py-4 px-4 flex items-center gap-2 lg:gap-4 text-black-500 rounded text-sm pl-8">
                <img src={settings.bodyStyleIcon} alt="" className="w-6 lg:w-8" />
                <span>
                  RV Class - <b>{item[InventoryField.body]}</b>
                </span>
              </div>
              <div className="bg-blue-100 py-4 px-4 flex items-center gap-2 lg:gap-4 text-black-500 rounded text-sm pl-8">
                <img src={settings.fuelTypeIcon} alt="" className="w-6 lg:w-8" />
                <span>
                  Fuel Type - <b>{item[InventoryField.fuelType]}</b>
                </span>
              </div>
              <div className="bg-blue-100 py-4 px-4 flex items-center gap-2 lg:gap-4 text-black-500 rounded text-sm pl-8">
                <img src={settings.mileageIcon} alt="" className="w-6 lg:w-8" />
                <span>
                  Mileage - <b>{item[InventoryField.mileage]}</b>
                </span>
              </div>
              <div className="bg-blue-100 py-4 px-4 flex items-center gap-2 lg:gap-4 text-black-500 rounded text-sm pl-8">
                <img src={settings.slideoutsIcon} alt="" className="w-6 lg:w-8" />
                <span>
                  Slideouts - <b>{item[InventoryField.slideOutsCount]}</b>
                </span>
              </div>
              <div className="bg-blue-100 py-4 px-4 flex items-center gap-2 lg:gap-4 text-black-500 rounded text-sm pl-8">
                <img src={settings.lengthIcon} alt="" className="w-6 lg:w-8" />
                <span>
                  Length - <b>{item[InventoryField.length]}</b>
                </span>
              </div>
              <div className="bg-blue-100 py-4 px-4 flex items-center gap-2 lg:gap-4 text-black-500 rounded text-sm pl-8">
                <img src={settings.engineIcon} alt="" className="w-6 lg:w-8" />
                <span>
                  Engine - <b>{item[InventoryField.engine]}</b>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface MobileTabletOverlayProps {
  item: IInventory;
  closeOverlay: () => void;
}

const MobileTabletOverlay = ({ closeOverlay, item }: MobileTabletOverlayProps) => {
  const { isFavorite, toggleFavorite } = useIsFavorite(item);
  const { canShare, handleShare } = useShareInventory(item);

  return (
    <div className="fixed w-screen h-screen top-0 left-0 z-50 bg-white flex flex-col lg:hidden">
      <div className="flex border-b border-black-200 justify-between px-4 py-3.5 items-center">
        <button type="button" onClick={closeOverlay}>
          <FontAwesomeIcon icon={faArrowLeft} className="size-5" />
        </button>
        <div className="flex items-center gap-3">
          {canShare && (
            <button type="button" onClick={handleShare}>
              <FontAwesomeIcon icon={faShareFromSquare} className="size-5" />
            </button>
          )}
          <button className={isFavorite ? 'text-red-500' : 'hover:text-red-500'} onClick={toggleFavorite} type="button">
            <FontAwesomeIcon icon={isFavorite ? faHeartSolid : faHeartOutline} className="size-5" />
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-2 flex-1 overflow-y-scroll">
        {item[InventoryField.imageCloudinaryIds].map((publicId) => (
          <img
            key={publicId}
            alt=""
            src={getImageUrl(publicId, INVENTORY_BIG_IMAGE_WIDTH, INVENTORY_BIG_IMAGE_HEIGHT)}
          />
        ))}
      </div>
    </div>
  );
};

interface PdpImageSliderProps {
  item: IInventory;
}

export const PdpImageSlider = ({ item }: PdpImageSliderProps) => {
  const [index, setIndex] = useState(0);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  const onIndexChange = (index: number) => {
    setIndex(index);

    if (thumbnailsRef.current) {
      const left = (index - 1) * PDP_MINIATURE_WIDTH_REM * 16 + (index - 1) * 8;
      thumbnailsRef.current.scrollTo({ left, behavior: 'smooth' });
    }
  };

  return (
    <>
      <div
        className="relative h-full"
        style={{
          aspectRatio: `${INVENTORY_BIG_IMAGE_WIDTH} / ${INVENTORY_BIG_IMAGE_HEIGHT}`,
        }}
      >
        <InventoryImageSlider
          className="absolute top-0 left-0 size-full [&_.slick-slider]:size-full [&_.slick-list]:size-full [&_.slick-track]:size-full"
          height={INVENTORY_BIG_IMAGE_HEIGHT}
          width={INVENTORY_BIG_IMAGE_WIDTH}
          images={item[InventoryField.imageCloudinaryIds]}
          index={index}
          onIndexChange={onIndexChange}
          swipe
          onClick={() => {
            setIsOverlayOpen(true);
          }}
        />

        <ImageActions item={item} />

        {isOverlayOpen && (
          <>
            <MobileTabletOverlay item={item} closeOverlay={() => setIsOverlayOpen(false)} />
            <DesktopOverlay item={item} closeOverlay={() => setIsOverlayOpen(false)} />
          </>
        )}
      </div>

      <div className="hidden md:flex px-2.5 gap-2 overflow-x-auto lg:px-0 max-lg:pt-4" ref={thumbnailsRef}>
        {item[InventoryField.imageCloudinaryIds].map((publicId, i) => (
          <div key={publicId} className="bg-black shrink-0">
            <img
              alt=""
              src={getImageUrl(publicId, INVENTORY_BIG_IMAGE_WIDTH, INVENTORY_BIG_IMAGE_HEIGHT)}
              className={twMerge('object-cover cursor-pointer w-full', index === i && 'p-[3px]')}
              onClick={() => setIndex(i)}
              role="presentation"
              style={{ width: `${PDP_MINIATURE_WIDTH_REM}rem` }}
            />
          </div>
        ))}
      </div>
    </>
  );
};
